import { LitElement, html, css } from 'lit';
import { customElement } from 'lit/decorators.js';
import { styles as sharedStyles } from '../../styles/shared-styles';
import '@shoelace-style/shoelace/dist/components/card/card.js';
import { writeMdTemplate } from '../../utility/markdown';

// Public terms of use. Anonymous-accessible so Google's OAuth
// consent screen reviewer can reach it directly. Linked from the
// login page footer + the About page.
//
// Drafted as plain-English boilerplate. NOT a substitute for legal
// review; placeholders are flagged and the user has been told to
// have counsel review before going live.
@customElement('app-terms')
export class AppTerms extends LitElement {
  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
        max-width: 760px;
        margin: 0 auto;
        padding: 24px 20px 48px;
        box-sizing: border-box;
      }
      sl-card {
        display: block;
      }
      h1,
      h2 {
        font-family: var(--font-display, system-ui);
      }
      h1 {
        margin-top: 0;
      }
      h2 {
        margin-top: 1.8rem;
      }
      .meta {
        margin-bottom: 1.5rem;
        font-size: 0.85rem;
        color: var(--sl-color-neutral-600);
      }
    `,
  ];

  render() {
    const markdown = writeMdTemplate`${'ref:terms:body'}
    # Terms of Use

    **Last updated: 2026-05-23**

    These terms govern your use of the Pag-asenso point-of-sale service ("the Service"). By creating an account or signing in you agree to them. If you do not agree, do not use the Service.

    ## 1. Beta / test status

    The Service is currently in active development and is offered on an **as-is, as-available basis with no warranty, express or implied**. Features may change, be added, or be removed at any time. Data may be lost in the event of a bug, outage, or schema migration during this period. **Test before you commit operationally.**

    ## 2. Eligibility and account

    You must be at least the age of majority in your jurisdiction and authorised to bind the business you are operating the Service for. You are responsible for maintaining the confidentiality of your account credentials and for everything that happens under your account.

    ## 3. Acceptable use

    You agree not to:

    - Use the Service for any unlawful purpose.
    - Attempt to gain unauthorised access to any account, shop, or system component.
    - Probe, scan, or test the vulnerability of the Service without prior written permission.
    - Interfere with or disrupt the Service or its underlying infrastructure.
    - Reverse engineer, decompile, or attempt to extract source code, except to the extent the law expressly permits.
    - Use the Service to handle data you are not legally permitted to handle.

    ## 4. Your data

    You retain ownership of the shop data, menu, sales records, and other content you enter ("Your Data"). You grant Pag-asenso a limited licence to process Your Data for the sole purpose of operating the Service for you. Pag-asenso does not sell or share Your Data with third parties. See the **Privacy Policy** for full details.

    ## 5. Data retention and export

    - While your subscription is active, Your Data is retained.
    - After a subscription lapses, a **30-day grace period** applies. After that grace period, Your Data is **not guaranteed to be retained and may be deleted without further notice.** Do not rely on the Service as your only copy of business-critical records.
    - Before the grace period ends, you may request a one-time JSON export of Your Data. A **per-download service fee** applies (current rates quoted on request). Contact **support@pag-asenso.com**.

    ## 6. Fees and subscriptions

    Where a paid tier applies, fees are billed in advance. Tier features and limits are described in-app and may be revised; material changes will be announced before they take effect on your account.

    ## 7. Service availability

    We aim for high availability but do not promise uninterrupted service. Scheduled maintenance, third-party outages (cloud provider, network, identity provider), or factors outside our control may cause downtime. We are not liable for any loss arising from such interruption.

    ## 8. Limitation of liability

    To the maximum extent permitted by law:

    - The Service is provided **"as is" and "as available"** with no warranties of any kind, whether express, implied, statutory, or otherwise, including warranties of merchantability, fitness for a particular purpose, or non-infringement.
    - Pag-asenso, its operators, and contributors are **not liable for any direct, indirect, incidental, special, consequential, or exemplary loss or damage** — including loss of sales, loss of profit, loss of data, loss of goodwill, or business interruption — arising out of or related to your use of the Service.
    - **You use the Service at your own risk.** You are responsible for your own backups, your own reconciliation of your books, and your own compliance with applicable tax, accounting, and consumer-protection law.
    - Where liability cannot be excluded by law, the total aggregate liability of Pag-asenso to you is capped at the fees you have paid for the Service in the three months preceding the event giving rise to the claim, or **PHP 1,000**, whichever is greater.

    ## 9. Indemnity

    You agree to indemnify and hold harmless Pag-asenso and its operators against any third-party claim arising out of your breach of these terms, your misuse of the Service, or your violation of any law in connection with your use of the Service.

    ## 10. Intellectual property

    The Pag-asenso name, marks, software, and design are owned by their respective creators. No licence is granted to you to use the marks or to redistribute the software except as expressly stated.

    ## 11. Termination

    You may stop using the Service at any time by deleting your account from the in-app settings. We may suspend or terminate your access if you breach these terms, if your subscription lapses past the grace period, or if continuing to provide the Service to you would be unlawful. The clauses that by their nature should survive termination (limitation of liability, indemnity, data retention treatment) do survive.

    ## 12. Changes to these terms

    We may update these terms. Material changes will be announced via in-app notice or email and will become effective on the date stated in the notice. Continued use after that date constitutes acceptance.

    ## 13. Governing law

    These terms are governed by the laws of the **Republic of the Philippines** (placeholder pending counsel review). Any dispute that cannot be resolved informally will be subject to the exclusive jurisdiction of the courts of that jurisdiction.

    ## 14. Contact

    Email **support@pag-asenso.com** for any question about these terms.
    `;

    return html`
      <sl-card>
        <div class="meta">
          You can find the matching Privacy Policy at
          <a href="/privacy">/privacy</a>.
        </div>
        ${markdown}
      </sl-card>
    `;
  }
}
